import { Fragment } from "react";
import Title from "../typography/Title";
import Description from "../typography/Description";
import {
  UsersThree,
  MagnifyingGlass,
  VideoCamera,
  PaperPlaneTilt,
  PlugsConnected,
  ShieldCheck,
  Headset,
  CreditCard,
  Check,
  Minus,
} from "@phosphor-icons/react/dist/ssr";

const PLAN_NAMES = ["Free Trial", "Starter", "Team", "Enterprise"] as const;

type Cell = string; // "✓" | "—" | a plain value like "1 credit/profile"

type FeatureRow = {
  feature: string;
  values: [Cell, Cell, Cell, Cell];
};

type Section = {
  label: string;
  icon: typeof UsersThree;
  note?: string;
  rows: FeatureRow[];
};

// Sourcing rows first, then screening — same pipeline order used elsewhere
// on this page. Values current as of 2026-08-30; update alongside Packages
// and TokenComparison when plan details change.
const SECTIONS: Section[] = [
  {
    label: "Team & Access",
    icon: UsersThree,
    rows: [
      { feature: "Users", values: ["1", "1", "2–5", "Custom"] },
      { feature: "Role-based access", values: ["—", "—", "✓", "✓"] },
    ],
  },
  {
    label: "AI Talent Sourcing Agent",
    icon: MagnifyingGlass,
    rows: [
      { feature: "Open roles", values: ["Unlimited", "Unlimited", "Unlimited", "Unlimited"] },
      { feature: "Search across 90M+ profiles", values: ["✓", "✓", "✓", "✓"] },
      { feature: "AI Filters", values: ["✓", "✓", "✓", "✓"] },
      { feature: "Mobility Signals", values: ["✓", "✓", "✓", "✓"] },
      { feature: "Profile downloads (PDF)", values: ["Unlimited", "Unlimited", "Unlimited", "Unlimited"] },
      { feature: "Contact email enrichment", values: ["1 credit", "1 credit", "1 credit", "✓"] },
      { feature: "Contact phone enrichment", values: ["2 credits", "2 credits", "2 credits", "✓"] },
    ],
  },
  {
    label: "AI Screening Agent",
    icon: VideoCamera,
    note: "Priced in AI-Credits — see the AI-Credit Usage Table above for the exact cost per action.",
    rows: [
      { feature: "AI Video Interviews", values: ["✓", "✓", "✓", "✓"] },
      { feature: "AI Phone Screening", values: ["✓", "✓", "✓", "✓"] },
      { feature: "Coding Assessments", values: ["✓", "✓", "✓", "✓"] },
      { feature: "Aptitude / Psychometric Tests", values: ["✓", "✓", "✓", "✓"] },
      { feature: "Resume Parsing", values: ["✓", "✓", "✓", "Free"] },
      { feature: "Interview languages", values: ["2", "40+", "40+", "40+"] },
    ],
  },
  {
    label: "Outreach",
    icon: PaperPlaneTilt,
    rows: [
      { feature: "Multi-channel outreach (Email/Phone/WhatsApp)", values: ["—", "✓", "✓", "✓"] },
      { feature: "Branded outreach", values: ["—", "—", "✓", "✓"] },
    ],
  },
  {
    label: "Integrations",
    icon: PlugsConnected,
    rows: [
      { feature: "ATS / HRMS integration", values: ["—", "—", "—", "✓"] },
      { feature: "Custom integrations", values: ["—", "—", "—", "✓"] },
    ],
  },
  {
    label: "Security & Compliance",
    icon: ShieldCheck,
    rows: [
      { feature: "Cheating detection / proctoring", values: ["✓", "✓", "✓", "✓"] },
      { feature: "No AI training on your data", values: ["✓", "✓", "✓", "✓"] },
      { feature: "Compliance readiness", values: ["—", "—", "—", "✓"] },
      { feature: "GDPR compliant", values: ["—", "✓", "✓", "✓"] },
      { feature: "ISO 27001", values: ["—", "✓", "✓", "✓"] },
      { feature: "VAPT report", values: ["—", "—", "—", "✓"] },
      { feature: "SAML / SSO", values: ["—", "—", "✓", "✓"] },
      { feature: "On-Prem Deployment", values: ["—", "—", "—", "✓"] },
    ],
  },
  {
    label: "Support",
    icon: Headset,
    rows: [
      { feature: "Support channels", values: ["Email", "Email", "Email, Phone", "Email, Chat & Phone"] },
      { feature: "Dedicated account manager", values: ["—", "—", "—", "✓"] },
      { feature: "Custom assessments", values: ["—", "—", "—", "✓"] },
    ],
  },
  {
    label: "Billing",
    icon: CreditCard,
    rows: [
      { feature: "Card payment", values: ["—", "✓", "✓", "Card/NEFT/RTGS"] },
    ],
  },
];

const categoryCounts = SECTIONS.reduce<Record<string, number>>((acc, section) => {
  acc[section.label] = section.rows.length;
  return acc;
}, {});

const ACCENT = "#41C3D5";

const CellValue = ({ value }: { value: Cell }) => {
  if (value === "✓") {
    return (
      <div className="flex justify-center">
        <Check size={16} weight="bold" style={{ color: ACCENT }} />
      </div>
    );
  }
  if (value === "—") {
    return (
      <div className="flex justify-center">
        <Minus size={14} weight="bold" className="text-gray-300" />
      </div>
    );
  }
  return <span className="text-sm text-gray-200">{value}</span>;
};

const FeaturesComparison = () => {
  return (
    <section className="relative z-10 px-4 py-16 sm:px-6 lg:px-10 xl:px-16">
      <Title size="lg">Compare Plans</Title>
      <Description className="mx-auto mt-4 max-w-2xl">
        Every plan shares the same AI Talent Sourcing and Screening agents — the
        difference is scale, outreach, and what's built in versus custom.
      </Description>

      <div className="mx-auto mt-8 max-w-5xl">
        <div className="overflow-x-auto rounded-2xl border border-white/[0.08] bg-gray-600">
          <table className="w-full min-w-[760px] border-collapse">
            <thead>
              <tr className="border-b border-white/[0.08] bg-gray-650">
                <th className="sticky left-0 z-[3] bg-gray-650 px-3 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-350">
                  Category
                </th>
                <th className="sticky left-[68px] z-[3] bg-gray-650 px-4 py-4 text-left text-sm font-semibold text-white">
                  Feature
                </th>
                {PLAN_NAMES.map((name) => (
                  <th
                    key={name}
                    className="px-4 py-4 text-center text-sm font-semibold text-white"
                  >
                    {name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {SECTIONS.map((section, sIdx) => {
                const SectionIcon = section.icon;
                // Extra-thick top border marking the boundary between
                // category groups (not on the very first group).
                const sectionDivider = sIdx !== 0 ? "border-t-2 border-t-teal-500/30" : "";
                return (
                  <Fragment key={section.label}>
                    {section.note && (
                      <tr>
                        <td colSpan={6} className={`bg-gray-600 px-4 pb-2 pt-5 ${sectionDivider}`}>
                          <p className="text-xs italic text-gray-350">{section.note}</p>
                        </td>
                      </tr>
                    )}
                    {section.rows.map((row, rIdx) => {
                      const rowBg = rIdx % 2 === 0 ? "bg-gray-600" : "bg-gray-650";
                      const rowDivider = rIdx === 0 && !section.note ? sectionDivider : "";
                      return (
                      <tr key={row.feature} className={rowBg}>
                        {rIdx === 0 && (
                          <td
                            rowSpan={categoryCounts[section.label]}
                            className={`sticky left-0 z-[1] border-r border-white/[0.08] bg-gray-600 px-3 py-4 text-center align-middle ${rowDivider}`}
                          >
                            <div className="flex flex-col items-center gap-1.5">
                              <SectionIcon size={16} weight="bold" style={{ color: ACCENT }} />
                              <p
                                className="text-[10px] font-semibold uppercase leading-tight tracking-wider"
                                style={{ color: ACCENT }}
                              >
                                {section.label}
                              </p>
                            </div>
                          </td>
                        )}
                        <td className={`sticky left-[68px] z-[1] ${rowBg} px-4 py-4 text-sm font-medium text-white ${rowDivider}`}>
                          {row.feature}
                        </td>
                        {row.values.map((value, vIdx) => (
                          <td key={vIdx} className={`px-4 py-4 text-center ${rowDivider}`}>
                            <CellValue value={value} />
                          </td>
                        ))}
                      </tr>
                      );
                    })}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        <p className="mt-4 text-center text-xs text-gray-300">
          Scroll to see all plans on smaller screens.
        </p>
      </div>
    </section>
  );
};

export default FeaturesComparison;
